import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  KOMPIL_FROM_VSCODE,
  kompilPluginFor,
  PLUGIN_CATALOG,
  type PluginId,
  type PluginListing,
} from "@ide/shared";
import type { CodeEngineExtension, GalleryExtension, GallerySort } from "@ide/shared/desktop";
import { desktop } from "@/desktop/bridge";
import { del, get, put } from "@/lib/api";
import { refreshEngine, useCodeEngine } from "@/workspace/code-engine";
import { pluginKeys, usePlugins } from "../use-plugins";
import { vscodeKeys } from "../vscode-imported";
import { queryClient } from "@/lib/queries";

/**
 * Dados da tela de Extensões (igual à do VS Code): as instaladas no motor VS Code, o catálogo da
 * Open VSX e os plugins próprios do Kompil (os que não existem no VS Code). Instalar, habilitar,
 * desabilitar e desinstalar uma extensão que tem recurso equivalente no editor do Kompil liga ou
 * desliga esse recurso junto — o plugin do Kompil não aparece duplicado.
 */
export const engineApi = () => desktop?.codeEngine ?? null;

export const extKeys = {
  installed: ["engine", "extensions"] as const,
  updates: ["engine", "updates"] as const,
  gallery: (query: string, sort: GallerySort | undefined, category: string | undefined) =>
    ["gallery", query, sort ?? null, category ?? null] as const,
  galleryDetails: (id: string) => ["gallery", "details", id] as const,
  localDetails: (id: string) => ["engine", "details", id] as const,
};

/** Item da lista: extensão do VS Code (motor + Open VSX) ou plugin próprio do Kompil. */
export type ExtItem =
  | {
      kind: "vscode";
      id: string;
      name: string;
      publisher: string;
      verified: boolean;
      description: string;
      icon: string | null;
      version: string;
      downloads: number | null;
      rating: number | null;
      reviews: number;
      installed: CodeEngineExtension | null;
      /** Versão nova na Open VSX. */
      update: string | null;
      /** Recurso equivalente no editor do Kompil. */
      kompil: PluginId | null;
    }
  | { kind: "kompil"; id: string; plugin: PluginListing };

export const kompilItem = (plugin: PluginListing): ExtItem => ({ kind: "kompil", id: plugin.id, plugin });

export const itemKey = (i: ExtItem) => `${i.kind}:${i.id}`;

/** O item escolhido com o estado atual (instalou, desabilitou, desinstalou…). */
export function useLiveItem(item: ExtItem | null): ExtItem | null {
  const installed = useInstalledExtensions(item?.kind === "vscode");
  const plugins = usePlugins(item?.kind === "kompil");
  if (!item) return null;
  if (item.kind === "kompil")
    return { ...item, plugin: plugins.data?.find((p) => p.id === item.id) ?? item.plugin };
  if (!installed.data) return item;
  const now = installed.data.find((e) => e.id === item.id) ?? null;
  return {
    ...item,
    installed: now,
    version: now?.version ?? item.version,
    icon: item.icon ?? now?.icon ?? null,
    update: now ? item.update : null,
  };
}

export function fromInstalled(e: CodeEngineExtension, update: string | null = null): ExtItem {
  return {
    kind: "vscode",
    id: e.id,
    name: e.name ?? e.id,
    publisher: e.publisher ?? e.id.split(".")[0]!,
    verified: false,
    description: e.description ?? "",
    icon: e.icon ?? null,
    version: e.version,
    downloads: null,
    rating: null,
    reviews: 0,
    installed: e,
    update,
    kompil: kompilPluginFor(e.id),
  };
}

export function fromGallery(g: GalleryExtension, installed: CodeEngineExtension | null): ExtItem {
  return {
    kind: "vscode",
    id: g.id,
    name: installed?.name ?? g.displayName,
    publisher: g.publisher,
    verified: g.verified,
    description: g.description || installed?.description || "",
    icon: g.iconUrl ?? installed?.icon ?? null,
    version: installed?.version ?? g.version,
    downloads: g.downloads,
    rating: g.rating,
    reviews: g.reviews,
    installed,
    update: installed && g.version !== installed.version && installed.origin === "engine" ? g.version : null,
    kompil: kompilPluginFor(g.id),
  };
}

/** Plugins próprios do Kompil: os que não vêm de uma extensão do VS Code. */
export const kompilOnly = (list: PluginListing[]) => list.filter((p) => !(p.id in KOMPIL_FROM_VSCODE));

export function useInstalledExtensions(enabled = true) {
  return useQuery({
    queryKey: extKeys.installed,
    queryFn: async () => (await engineApi()?.extensions()) ?? [],
    enabled: enabled && Boolean(engineApi()),
    staleTime: 15_000,
  });
}

export function useExtensionUpdates(enabled = true) {
  return useQuery({
    queryKey: extKeys.updates,
    queryFn: async () => (await engineApi()?.updates?.()) ?? {},
    enabled: enabled && Boolean(engineApi()?.updates),
    staleTime: 60 * 60_000,
  });
}

export function useGallery(query: string, sort?: GallerySort, category?: string, enabled = true) {
  return useQuery({
    queryKey: extKeys.gallery(query, sort, category),
    queryFn: async () =>
      (await engineApi()?.gallery?.search({
        query,
        size: 50,
        ...(sort ? { sort } : {}),
        ...(category ? { category } : {}),
      })) ?? {
        total: 0,
        extensions: [],
      },
    enabled: enabled && Boolean(engineApi()?.gallery),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useGalleryDetails(id: string | null) {
  return useQuery({
    queryKey: extKeys.galleryDetails(id ?? ""),
    queryFn: async () => (await engineApi()?.gallery?.details(id!)) ?? null,
    enabled: Boolean(id && engineApi()?.gallery),
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

export function useLocalDetails(id: string | null, installed: boolean) {
  return useQuery({
    queryKey: extKeys.localDetails(id ?? ""),
    queryFn: async () => (await engineApi()?.extensionDetails?.(id!)) ?? null,
    enabled: Boolean(id && installed && engineApi()?.extensionDetails),
    staleTime: 60_000,
  });
}

type Action =
  | { type: "install"; id: string; version?: string }
  | { type: "uninstall"; id: string }
  | { type: "enable"; id: string }
  | { type: "disable"; id: string };

const isThemePlugin = (id: string) =>
  PLUGIN_CATALOG.find((p) => p.id === id)?.permissions.some(
    (perm) => perm === "editor.theme" || perm === "ui.theme",
  ) ?? false;

/** O motor precisa estar instalado para instalar extensões (baixa uma vez). */
async function ensureEngine(): Promise<boolean> {
  const api = engineApi();
  if (!api) return false;
  const status = await api.status();
  if (["ready", "running", "starting", "importing"].includes(status.state)) return true;
  if (status.state === "unsupported") return false;
  toast.loading("Preparando o motor VS Code (uma vez só)…", { id: "engine-prepare", duration: Infinity });
  const after = await api.install();
  toast.dismiss("engine-prepare");
  return ["ready", "running"].includes(after.state);
}

/**
 * Liga/desliga o recurso do editor do Kompil que corresponde à extensão (Prettier, Vim, temas…).
 * Desligar só quando nenhuma outra extensão habilitada pede o mesmo recurso.
 */
async function syncKompilPlugin(
  plugin: PluginId,
  on: boolean,
  installed: CodeEngineExtension[],
  plugins: PluginListing[] | undefined,
) {
  const current = plugins?.find((p) => p.id === plugin);
  if (on) {
    if (current?.installed && current.enabled) return;
    // Tema: só um fica ligado por vez (o novo vence).
    if (isThemePlugin(plugin))
      for (const other of plugins ?? [])
        if (other.id !== plugin && other.installed && other.enabled && isThemePlugin(other.id))
          await put(`/me/plugins/${other.id}`, { enabled: false });
    await put(`/me/plugins/${plugin}`, { enabled: true });
    return;
  }
  const stillWanted = installed.some((e) => e.enabled !== false && kompilPluginFor(e.id) === plugin);
  if (!stillWanted && current?.installed) await del(`/me/plugins/${plugin}`);
}

/**
 * Temas de cor de uma extensão do motor (Tokyo Night, Catppuccin…) entram na conta junto com os
 * importados do VS Code: aparecem no seletor de temas e pintam a IDE inteira (interface, editor,
 * terminal e as vistas do motor), em qualquer tela e em qualquer computador. Ao instalar, o primeiro
 * tema da extensão já passa a valer (como o VS Code, que abre o seletor na hora). Devolve o rótulo
 * do tema ativado, se houver.
 */
async function syncEngineTheme(
  id: string,
  mode: "install" | "keep" | "remove",
): Promise<{ changed: boolean; applied: string | null }> {
  const none = { changed: false, applied: null };
  const api = engineApi();
  if (!api?.extensionContent || kompilPluginFor(id)) return none;
  const key = id.toLowerCase();
  if (mode === "remove") {
    const listed = await get<{ extensions: Array<{ id: string; themes: unknown[] }> }>(
      "/me/vscode-extensions",
    );
    // Só sai da conta a extensão de temas (snippets importados do VS Code continuam).
    if (!listed.extensions.some((e) => e.id === key && e.themes.length)) return none;
    await del(`/me/vscode-extensions/${encodeURIComponent(key)}`);
    return { changed: true, applied: null };
  }
  const content = await api.extensionContent(id);
  if (!content?.themes.length) return none;
  const first = content.themes[0]!;
  await put("/me/vscode-extensions", {
    source: "kompil",
    extensions: [content],
    ...(mode === "install" ? { activate: { extensionId: content.id, label: first.label } } : {}),
  });
  return { changed: true, applied: mode === "install" ? first.label : null };
}

/**
 * Temas de extensões que já estavam no motor antes (ou instaladas por fora) e ainda não estão na
 * conta: entram sem trocar o tema atual. Roda ao abrir o seletor de temas.
 */
export async function syncInstalledEngineThemes(): Promise<void> {
  const api = engineApi();
  if (!api?.extensionContent) return;
  const [installed, listed] = await Promise.all([
    api.extensions().catch(() => [] as CodeEngineExtension[]),
    get<{ extensions: Array<{ id: string }> }>("/me/vscode-extensions").catch(() => null),
  ]);
  if (!listed) return;
  const known = new Set(listed.extensions.map((e) => e.id));
  const missing = installed.filter(
    (e) =>
      e.enabled !== false &&
      !known.has(e.id.toLowerCase()) &&
      !kompilPluginFor(e.id) &&
      (e.categories ?? []).some((c) => /theme/i.test(c)),
  );
  let changed = false;
  for (const e of missing.slice(0, 20))
    if ((await syncEngineTheme(e.id, "keep").catch(() => null))?.changed) changed = true;
  if (changed) await queryClient.invalidateQueries({ queryKey: vscodeKeys.list });
}

/** "Usar" um tema de uma extensão instalada: passa a valer na IDE inteira (e nas outras máquinas). */
export async function applyExtensionTheme(id: string, label: string): Promise<void> {
  const plugin = kompilPluginFor(id);
  try {
    if (plugin) {
      // Tema que o Kompil já traz (Dracula, Nord…): liga o plugin equivalente (e só ele).
      await syncKompilPlugin(plugin, true, [], queryClient.getQueryData<PluginListing[]>(pluginKeys.all));
      await del("/me/vscode-extensions/theme");
      await queryClient.invalidateQueries({ queryKey: pluginKeys.all });
      await queryClient.invalidateQueries({ queryKey: vscodeKeys.list });
    } else {
      await syncEngineTheme(id, "keep");
      await put("/me/vscode-extensions/theme", { extensionId: id.toLowerCase(), label });
      await queryClient.invalidateQueries({ queryKey: vscodeKeys.list });
    }
    toast.success(`Tema "${label}" aplicado em toda a IDE`, { id: `theme-${id}` });
  } catch (err) {
    toast.error("Não deu para usar este tema", {
      id: `theme-${id}`,
      description: err instanceof Error ? err.message : String(err),
    });
  }
}

const LABEL: Record<Action["type"], [string, string]> = {
  install: ["Instalando", "instalada"],
  uninstall: ["Desinstalando", "desinstalada"],
  enable: ["Habilitando", "habilitada"],
  disable: ["Desabilitando", "desabilitada"],
};

/** Instalar, desinstalar, habilitar e desabilitar uma extensão do motor (como no VS Code). */
export function useExtensionAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Action & { name?: string }) => {
      const api = engineApi();
      if (!api) throw new Error("As extensões do VS Code rodam no Kompil Desktop.");
      const name = a.name ?? a.id;
      const toastId = `ext-${a.id}`;
      toast.loading(`${LABEL[a.type][0]} ${name}…`, { id: toastId, duration: Infinity });
      try {
        if (a.type === "install") {
          if (!(await ensureEngine())) throw new Error("O motor VS Code não ficou pronto. Tente de novo.");
          const res = await api.installExtension(a.id, a.version);
          if (!res.ok) throw new Error(res.error ?? "Não deu para instalar.");
        } else if (a.type === "uninstall") {
          const res = await api.uninstallExtension?.(a.id);
          if (res && !res.ok) throw new Error(res.error ?? "Não deu para desinstalar.");
        } else {
          const res = await api.setExtensionEnabled?.(a.id, a.type === "enable");
          if (res && !res.ok) throw new Error("Não deu para mudar a extensão.");
        }
        const installed = await api.extensions();
        qc.setQueryData(extKeys.installed, installed);
        const plugin = kompilPluginFor(a.id);
        if (plugin)
          await syncKompilPlugin(
            plugin,
            a.type === "install" || a.type === "enable",
            installed,
            qc.getQueryData<PluginListing[]>(pluginKeys.all),
          ).catch(() => null);
        // Tema de cor: vale na IDE inteira (entra na conta e já fica ativo ao instalar).
        const theme = await syncEngineTheme(
          a.id,
          a.type === "install" ? "install" : a.type === "enable" ? "keep" : "remove",
        ).catch(() => null);
        if (theme?.changed) void qc.invalidateQueries({ queryKey: vscodeKeys.list });
        const applied = theme?.applied;
        if (applied)
          toast.success(`Tema "${applied}" aplicado em toda a IDE`, {
            id: `theme-${a.id}`,
            description: "Troque quando quiser em Configurações › Temas.",
          });
        const status = await api.status();
        toast.success(`${name} ${LABEL[a.type][1]}`, {
          id: toastId,
          duration: 5_000,
          ...(status.restartNeeded
            ? {
                description: "Reinicie as extensões para valer nas janelas do motor abertas.",
                action: { label: "Reiniciar", onClick: () => void restartExtensions() },
              }
            : {}),
        });
      } catch (err) {
        toast.error(`Não deu: ${name}`, {
          id: toastId,
          description: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: extKeys.installed });
      void qc.invalidateQueries({ queryKey: extKeys.updates });
      void qc.invalidateQueries({ queryKey: pluginKeys.all });
      void refreshEngine();
    },
  });
}

/** Reinicia o servidor do motor: o que foi habilitado/desabilitado passa a valer. */
export async function restartExtensions() {
  const api = engineApi();
  if (!api?.restartExtensions) return;
  const id = "engine-restart";
  toast.loading("Reiniciando as extensões…", { id, duration: Infinity });
  const res = await api.restartExtensions();
  if (res.ok) toast.success("Extensões reiniciadas", { id, duration: 3_000 });
  else toast.error("Não deu para reiniciar as extensões", { id, description: res.error });
  await refreshEngine();
}

export const useRestartNeeded = () => useCodeEngine((s) => Boolean(s.status?.restartNeeded));

/** Plugin do Kompil pelo id (nome, ícone e descrição do catálogo, mesmo antes da lista chegar). */
export const catalogPlugin = (id: string) => PLUGIN_CATALOG.find((p) => p.id === id) ?? null;

/** "9,7 mi", "12 mil" (como a contagem de instalações da lista do VS Code). */
export const compactCount = (n: number) =>
  new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 }).format(n);

/** "28.765.660" (contagem completa da página da extensão). */
export const fullCount = (n: number) => n.toLocaleString("pt-BR");

export function formatBytes(n: number | null | undefined): string {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: v < 10 ? 2 : 1 })} ${units[u]}`;
}

/** Nome do editor de onde a extensão veio. */
export const ORIGIN_LABEL: Record<string, string> = {
  engine: "Instalada no motor do Kompil",
  vscode: "Do seu VS Code",
  "vscode-insiders": "Do seu VS Code Insiders",
  cursor: "Do seu Cursor",
  vscodium: "Do seu VSCodium",
  windsurf: "Do seu Windsurf",
  outro: "De outro editor",
};
